import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { passwordSchema } from '@authsemply/shared';
import { AuthCard } from '../components/AuthCard';
import { Alert } from '../components/Alert';
import { CompteBetaAvis } from '../components/CompteBetaAvis';
import { Field } from '../components/Field';
import { NouveauMotDePasse } from '../components/NouveauMotDePasse';
import { api, ApiError } from '../lib/api';
import { useAuthStore } from '../store/auth.store';

/** Ce que rend `GET /auth/mot-de-passe`. */
interface EtatMotDePasse {
  defini: boolean;
  definiLe: string | null;
}

const ICI = '/compte/mot-de-passe';

const date = (iso: string) =>
  new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    .format(new Date(iso))
    .replace(/^1 /, '1er ');

/**
 * « Changer mon mot de passe » (04/10/2026), depuis Mon compte — et depuis les
 * paramètres du compte de chaque outil SEmply, qui renvoient ici.
 *
 * L'ancien mot de passe est exigé ; les autres appareils sont déconnectés,
 * celui-ci reste connecté. Un compte ouvert par Google, Microsoft ou Apple n'a
 * pas de mot de passe : il en définit un par un lien envoyé à son adresse.
 */
export function ChangerMotDePassePage() {
  const navigate = useNavigate();
  const { user, loading, fetchMe } = useAuthStore();
  const [etat, setEtat] = useState<EtatMotDePasse | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (loading) return;
    // Pas de session : connexion, puis retour ICI (liste fermée du portail).
    if (!user) {
      navigate(`/login?${new URLSearchParams({ suite: ICI })}`);
      return;
    }
    if (user.mfaRequired) {
      navigate('/double-authentification');
      return;
    }
    let vivant = true;
    api
      .get<EtatMotDePasse>('/auth/mot-de-passe')
      .then((e) => {
        if (vivant) setEtat(e);
      })
      .catch((err: unknown) => {
        if (vivant) setErreur(err instanceof ApiError ? err.message : 'Portail injoignable.');
      });
    return () => {
      vivant = false;
    };
  }, [loading, user, navigate]);

  async function changer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErreur(null);
    setSucces(null);
    const formulaire = event.currentTarget;
    const donnees = new FormData(formulaire);
    const actuel = String(donnees.get('currentPassword') ?? '');
    const nouveau = String(donnees.get('newPassword') ?? '');
    const confirmation = String(donnees.get('confirmation') ?? '');

    const regles = passwordSchema.safeParse(nouveau);
    if (!regles.success) {
      setErreur(`Nouveau mot de passe : ${regles.error.issues[0]?.message ?? 'trop faible'}.`);
      return;
    }
    if (nouveau !== confirmation) {
      setErreur('Les deux saisies du nouveau mot de passe ne sont pas identiques.');
      return;
    }
    if (nouveau === actuel) {
      setErreur('Le nouveau mot de passe doit être différent de l’actuel.');
      return;
    }

    setEnvoi(true);
    try {
      const resultat = await api.post<{ modifie: true; sessionsFermees: number }>('/auth/mot-de-passe', {
        currentPassword: actuel,
        newPassword: nouveau,
      });
      formulaire.reset();
      setSucces(
        resultat.sessionsFermees > 0
          ? `Mot de passe modifié. ${resultat.sessionsFermees} autre(s) appareil(s) connecté(s) à votre compte ont été déconnecté(s).`
          : 'Mot de passe modifié.',
      );
      setEtat({ defini: true, definiLe: new Date().toISOString() });
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : 'Portail injoignable.');
    } finally {
      setEnvoi(false);
    }
  }

  /** Compte sans mot de passe : le lien de « mot de passe oublié », à sa propre adresse. */
  async function recevoirLien() {
    if (!user) return;
    setErreur(null);
    setEnvoi(true);
    await api.post('/auth/forgot-password', { email: user.email }).catch(() => undefined);
    setEnvoi(false);
    setSucces(`Un lien pour définir votre mot de passe vient d’être envoyé à ${user.email}.`);
  }

  if (loading || !user || (!etat && !erreur)) {
    return <AuthCard title="Chargement…">{null}</AuthCard>;
  }

  return (
    <AuthCard title="Changer mon mot de passe" subtitle={user.email}>
      <Alert message={erreur} />
      <Alert message={succes} variant="success" />

      {user.betaCompte ? (
        // Compte bêta partagé (03/10/2026) : le changement est refusé par l'API.
        <CompteBetaAvis />
      ) : etat && !etat.defini ? (
        <>
          <p style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
            Votre compte s’ouvre avec Google, Microsoft ou Apple : il n’a pas encore de mot de passe.
            Pour en définir un, recevez un lien à votre adresse.
          </p>
          <button className="btn" type="button" disabled={envoi} onClick={() => void recevoirLien()}>
            {envoi ? 'Envoi…' : 'Recevoir le lien'}
          </button>
        </>
      ) : etat ? (
        <form onSubmit={(e) => void changer(e)}>
          {etat.definiLe ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-color-light)', margin: '0 0 1rem' }}>
              Mot de passe actuel défini le {date(etat.definiLe)}.
            </p>
          ) : null}
          <Field
            label="Mot de passe actuel"
            name="currentPassword"
            type="password"
            autoComplete="current-password"
            required
          />
          <NouveauMotDePasse label="Nouveau mot de passe" name="newPassword" />
          <p style={{ fontSize: '0.8rem', color: 'var(--text-color-light)', margin: '0 0 1rem' }}>
            Les autres appareils connectés à votre compte seront déconnectés ; celui-ci reste connecté.
          </p>
          <button className="btn" type="submit" disabled={envoi}>
            {envoi ? 'Enregistrement…' : 'Changer le mot de passe'}
          </button>
        </form>
      ) : null}

      <div className="links">
        {etat?.defini && !user.betaCompte ? <Link to="/mot-de-passe-oublie">Mot de passe actuel oublié ?</Link> : null}
        <Link to="/compte">Mon compte</Link>
      </div>
    </AuthCard>
  );
}
